import { queryOptions } from '@tanstack/react-query';
import fetchClient from '@eureca/recruiter-api-sdk/client';
import type { GetJobs200, GetJobsQueryParams } from '@eureca/recruiter-api-sdk/types';
import type { JobsSearch } from './jobs-search.schema';

export type JobListEntry = GetJobs200['items'][number];
export type JobListResponse = GetJobs200;

/**
 * Error surfaced when the BFF refuses the request (auth/role/upstream failures).
 * `status` lets the UI branch — `403` renders the no-access fallback; everything
 * else falls into the generic retry block.
 */
export class JobsFetchError extends Error {
  readonly name = 'JobsFetchError';
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

/** Drop fields the BFF would reject (search <2 chars) or that are undefined. */
function paramsFromSearch(search: JobsSearch): GetJobsQueryParams {
  const effectiveSearch = search.search && search.search.length >= 2 ? search.search : undefined;
  return {
    ...(effectiveSearch ? { search: effectiveSearch } : {}),
    ...(search.companyId ? { companyId: search.companyId } : {}),
    // O BFF/Core seguem aceitando lista (outros chamadores); a tela manda UM.
    ...(search.programId ? { programId: [search.programId] } : {}),
    ...(search.status && search.status.length > 0 ? { status: search.status } : {}),
    ...(search.sort ? { sort: search.sort } : {}),
    dir: search.dir,
    page: search.page,
    pageSize: search.pageSize,
  };
}

async function fetchJobs(
  params: GetJobsQueryParams,
  signal?: AbortSignal
): Promise<JobListResponse> {
  const res = await fetchClient<JobListResponse>({
    method: 'GET',
    url: '/jobs',
    params,
    signal,
  });
  if (res.error) {
    throw new JobsFetchError(`Failed to load jobs (status ${res.error.status})`, res.error.status);
  }
  return res.data;
}

export function jobsQueryKey(search: JobsSearch) {
  return ['recruiter', 'jobs', 'page', paramsFromSearch(search)] as const;
}

export function jobsQueryOptions(search: JobsSearch) {
  const params = paramsFromSearch(search);
  return queryOptions({
    queryKey: jobsQueryKey(search),
    queryFn: ({ signal }) => fetchJobs(params, signal),
    staleTime: 60_000,
    // DEV-315 FR-7 — reaproveita os dados anteriores SÓ dentro do MESMO programa
    // (troca de página/status/busca fica suave). Ao trocar de programa, nada do
    // programa anterior fica na tela enquanto a nova busca não responde — uma
    // resposta atrasada da seleção anterior nunca é exibida sobre a atual.
    placeholderData: (previousData, previousQuery) => {
      const prevParams = previousQuery?.queryKey[3] as GetJobsQueryParams | undefined;
      const prevProgram = Array.isArray(prevParams?.programId)
        ? prevParams.programId[0]
        : prevParams?.programId;
      const nextProgram = Array.isArray(params.programId) ? params.programId[0] : params.programId;
      return prevProgram === nextProgram ? previousData : undefined;
    },
    retry: (failureCount, err) => {
      if (err instanceof JobsFetchError && err.status >= 400 && err.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
  });
}
